import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, ArrowLeft, User } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { api } from "@/lib/api";
import { blogMediaUrl } from "@/lib/blogMedia";
import { isHtmlBlogBody, resolveBlogBodyHtml } from "@/lib/blogBody";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

const IMG_LINE =
  /^https?:\/\/.+\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i;
const VIDEO_LINE =
  /^https?:\/\/.+\.(mp4|webm|mov)(\?.*)?$/i;

/** Legacy plain-text blocks + full HTML from rich editor. */
function BlogBody({ body }) {
  if (!body) return null;

  if (isHtmlBlogBody(body)) {
    const html = resolveBlogBodyHtml(body);
    return (
      <div
        className="blog-body-html prose-blog"
        data-testid="blog-body-html"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  const blocks = body.split(/\n\n+/);
  return (
    <div className="blog-body space-y-4 text-[16px] leading-[1.75] text-cm-navy">
      {blocks.map((block, i) => {
        const t = block.trim();
        if (!t) return null;
        if (t.startsWith("/api/cms/blog/media/")) {
          const lower = t.toLowerCase();
          if (/\.(mp4|webm|mov)(\?|$)/i.test(lower)) {
            return (
              <figure key={i} className="blog-video-embed">
                <video controls playsinline preload="metadata" crossOrigin="anonymous" src={blogMediaUrl(t)} />
              </figure>
            );
          }
          return (
            <figure key={i} className="blog-image-embed">
              <img src={blogMediaUrl(t)} alt="" crossOrigin="anonymous" referrerPolicy="no-referrer" />
            </figure>
          );
        }
        if (IMG_LINE.test(t)) {
          return (
            <figure key={i} className="blog-image-embed">
              <img src={blogMediaUrl(t)} alt="" crossOrigin="anonymous" referrerPolicy="no-referrer" />
            </figure>
          );
        }
        if (VIDEO_LINE.test(t)) {
          return (
            <figure key={i} className="blog-video-embed">
              <video controls playsinline preload="metadata" src={t} />
            </figure>
          );
        }
        if (t.startsWith("<")) {
          return (
            <div
              key={i}
              className="prose-blog"
              dangerouslySetInnerHTML={{ __html: resolveBlogBodyHtml(t) }}
            />
          );
        }
        return (
          <p key={i} className="whitespace-pre-wrap">
            {t}
          </p>
        );
      })}
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setError(false);
    api
      .get(`/api/cms/blog/posts/${encodeURIComponent(slug)}`)
      .then(({ data }) => setPost(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <Header />
      <div className="border-b border-[var(--cm-border)] bg-cm-surface/50">
        <div className="cm-container max-w-[800px] pt-24 pb-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[13px] font-semibold text-cm-navy bg-white border border-[var(--cm-border)] hover:border-cm-blue/40 hover:text-cm-blue transition-colors"
            data-testid="blog-back"
          >
            <ArrowLeft size={15} /> All articles
          </Link>
        </div>
      </div>
      <article className="cm-container max-w-[800px] pt-10 pb-20">
        {loading ? (
          <div className="animate-pulse space-y-6">
            <div className="aspect-[16/9] rounded-[16px] bg-cm-surface" />
            <div className="h-10 w-4/5 rounded bg-cm-surface" />
            <div className="h-4 w-40 rounded bg-cm-surface" />
            <div className="space-y-3 pt-4">
              <div className="h-4 w-full rounded bg-cm-surface" />
              <div className="h-4 w-full rounded bg-cm-surface" />
              <div className="h-4 w-3/4 rounded bg-cm-surface" />
            </div>
          </div>
        ) : error || !post ? (
          <div data-testid="blog-post-not-found" className="text-center py-12">
            <h1 className="text-2xl font-bold text-cm-navy">Article not found</h1>
            <p className="mt-2 text-cm-grey">This post may have been removed or is not published yet.</p>
            <Link
              to="/blog"
              className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-cm-blue hover:underline"
            >
              <ArrowLeft size={16} /> Back to blog
            </Link>
          </div>
        ) : (
          <>
            {post.thumbnail_url ? (
              <img
                src={blogMediaUrl(post.thumbnail_url)}
                alt=""
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                className="w-full rounded-[16px] aspect-[16/9] object-cover mb-8 border border-[var(--cm-border)]"
                data-testid="blog-post-hero"
              />
            ) : null}
            <h1
              className="text-cm-navy font-extrabold tracking-tight"
              style={{ fontSize: "clamp(28px, 4vw, 44px)", lineHeight: 1.15 }}
              data-testid="blog-post-title"
            >
              {post.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-[13px] text-cm-grey">
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={15} />
                {formatDate(post.published_at || post.created_at)}
              </span>
              {post.author_name ? (
                <span className="inline-flex items-center gap-1.5">
                  <User size={15} />
                  {post.author_name}
                </span>
              ) : null}
            </div>
            {post.excerpt ? (
              <p className="mt-6 text-[18px] text-cm-grey font-medium leading-relaxed border-l-4 border-cm-blue pl-4">
                {post.excerpt}
              </p>
            ) : null}
            <div className="mt-10">
              <BlogBody body={post.body} />
            </div>
            <div className="mt-14 pt-8 border-t border-[var(--cm-border)]">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-[14px] font-semibold text-cm-blue hover:underline"
              >
                <ArrowLeft size={16} /> More articles
              </Link>
            </div>
          </>
        )}
      </article>
      <Footer />
    </div>
  );
}
